import '@algolia/autocomplete-theme-classic'

import {AutocompleteOptions, autocomplete} from '@algolia/autocomplete-js'
import {
	Fragment,
	createElement,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import {usePagination, useSearchBox} from 'react-instantsearch-hooks'

import {BaseItem} from '@algolia/autocomplete-core'
import React from 'react'
import {SearchClient} from 'algoliasearch/lite'
import algoliasearch from 'algoliasearch'
import {createLocalStorageRecentSearchesPlugin} from '@algolia/autocomplete-plugin-recent-searches'
import {createQuerySuggestionsPlugin} from '@algolia/autocomplete-plugin-query-suggestions'
import {createRoot} from 'react-dom/client'

type SetInstantSearchUiStateOptions = {
	query: string
}

const algoliaClient = algoliasearch(
	process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
	process.env.NEXT_PUBLIC_ALGOLIA_API_KEY
)

export const Autocomplete: React.FC<
	Partial<AutocompleteOptions<BaseItem>> & {
		searchClient: SearchClient
		onSearch?: (query: string) => void
		className?: string
		searchAsYouType?: boolean
		qsIndex?: string
	}
> = ({
	className,
	onSearch,
	searchClient,
	searchAsYouType = true,
	qsIndex,
	...autocompleteProps
}) => {
	const autocompleteContainer = useRef<HTMLDivElement>(null)
	const panelRootRef = useRef(null)
	const rootRef = useRef<HTMLElement>(null)

	const {query, refine: setQuery} = useSearchBox()
	const {refine: setPage} = usePagination()
	const [instantSearchUiState, setInstantSearchUiState] =
		useState<SetInstantSearchUiStateOptions>({query})

	// Todo: This logic can be removed once all indices are created, than only use the /lite version
	const [hasQs, setHasQs] = useState(false)
	useEffect(() => {
		if (!qsIndex) return
		algoliaClient.initIndex(qsIndex).exists().then(setHasQs)
	}, [])

	useEffect(() => {
		setQuery(instantSearchUiState.query)
		setPage(0)
	}, [instantSearchUiState])

	const plugins = useMemo(() => {
		const recentSearches = createLocalStorageRecentSearchesPlugin({
			key: 'instantsearch',
			limit: 3,
			transformSource({source}) {
				return {
					...source,
					onSelect({item}) {
						setInstantSearchUiState({query: item.label})
					}
				}
			}
		})
		if (!hasQs) return [recentSearches]

		const querySuggestions = createQuerySuggestionsPlugin({
			searchClient,
			indexName: qsIndex,
			getSearchParams() {
				return recentSearches.data!.getAlgoliaSearchParams({
					hitsPerPage: 6
				})
			},
			transformSource({source}) {
				return {
					...source,
					sourceId: 'querySuggestionsPlugin',
					onSelect({item}) {
						setInstantSearchUiState({query: item.query})
					},
					getItems(params) {
						if (!params.state.query) {
							return []
						}

						return source.getItems(params)
					}
				}
			}
		})

		return [recentSearches, querySuggestions]
	}, [hasQs])

	useEffect(() => {
		if (!autocompleteContainer.current) {
			return
		}

		const autocompleteInstance = autocomplete({
			...autocompleteProps,
			container: autocompleteContainer.current,
			initialState: {query},
			onReset() {
				setInstantSearchUiState({query: ''})
			},
			onSubmit({state}) {
				setInstantSearchUiState({query: state.query})
				if (onSearch) onSearch(state.query)
			},
			onStateChange({prevState, state}) {
				if (!searchAsYouType) return
				if (prevState.query !== state.query) {
					setInstantSearchUiState({
						query: state.query
					})
					if (prevState.isOpen && !state.isOpen) {
						if (onSearch) onSearch(state.query)
					}
				}
			},
			renderer: {createElement, Fragment, render: () => {}},
			render({children}, root) {
				if (!panelRootRef.current || rootRef.current !== root) {
					rootRef.current = root
					panelRootRef.current?.unmount()
					panelRootRef.current = createRoot(root)
				}
				panelRootRef.current.render(children)
			},
			plugins
		})

		return () => autocompleteInstance.destroy()
	}, [plugins])

	return <div className={className} ref={autocompleteContainer} />
}
